import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";
import ImageField from "@/components/ImageField";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", published: true };
const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";

export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editFor, setEditFor] = useState(null);
  const [editCover, setEditCover] = useState("");

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blog", form);
      setForm(EMPTY);
      load();
      toast.success("Post published");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async (e, id) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.put(`/admin/blog/${id}`, {
        title: fd.get("title"), excerpt: fd.get("excerpt"), content: fd.get("content"),
        cover_image: editCover, published: fd.get("published") === "on",
      });
      setEditFor(null);
      load();
      toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  return (
    <div data-testid="admin-blog">
      <form onSubmit={add} className="bg-white border border-[#E5E5E2] p-6 mb-8 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">New Post</p>
        <input required placeholder="Post title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="blog-add-title" className={inputCls} />
        <input placeholder="Short excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          data-testid="blog-add-excerpt" className={inputCls} />
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Cover photo</p>
          <ImageField value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} testId="blog-add-cover" />
        </div>
        <textarea rows={6} placeholder="Write your story… (blank line = new paragraph)" value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-add-content" className={`${inputCls} resize-y`} />
        <button type="submit" data-testid="blog-add-submit"
          className="w-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] transition-colors">
          Publish Post
        </button>
      </form>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-white border border-[#E5E5E2] p-4">
            <div className="flex items-center gap-4">
              {p.cover_image && <img src={imgUrl(p.cover_image)} alt="" className="w-14 h-14 object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.title}</p>
                <p className="text-xs text-[#7A7A7A]">
                  {p.published ? "Published" : "Draft"} · {new Date(p.created_at).toLocaleDateString()}
                </p>
              </div>
              <button onClick={() => { const next = editFor === p.id ? null : p.id; setEditFor(next); if (next) setEditCover(p.cover_image || ""); }} data-testid={`post-edit-${p.id}`} className="p-2 border border-[#E5E5E2]">
                {editFor === p.id ? <X size={13} /> : <Pencil size={13} />}
              </button>
              <button onClick={() => remove(p.id)} data-testid={`post-delete-${p.id}`} className="p-2 border border-[#E5E5E2] text-red-500"><Trash2 size={13} /></button>
            </div>
            {editFor === p.id && (
              <form onSubmit={(e) => save(e, p.id)} className="mt-4 border-t border-[#E5E5E2] pt-4 space-y-3" data-testid={`post-edit-form-${p.id}`}>
                <input name="title" defaultValue={p.title} className={inputCls} />
                <input name="excerpt" defaultValue={p.excerpt} placeholder="Excerpt" className={inputCls} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Cover photo</p>
                  <ImageField value={editCover} onChange={setEditCover} testId={`post-cover-${p.id}`} />
                </div>
                <textarea name="content" rows={8} defaultValue={p.content} className={`${inputCls} resize-y`} />
                <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                  <input name="published" type="checkbox" defaultChecked={p.published} className="accent-[#1A1A1A] w-4 h-4" data-testid={`post-published-${p.id}`} />
                  Published (visible on the site)
                </label>
                <button type="submit" data-testid={`post-save-${p.id}`}
                  className="bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-2.5 hover:bg-[#4A4A4A] transition-colors">
                  Save Post
                </button>
              </form>
            )}
          </div>
        ))}
        {posts.length === 0 && <p className="text-sm text-[#7A7A7A] text-center py-10">No posts yet — write your first above.</p>}
      </div>
    </div>
  );
}
